/**
 * "Close enough to be the same number", for comparing two values that were both COMPUTED.
 *
 * Read this together with `cleanComputed` below, which handles the other half of the same
 * problem. The division of labour:
 *
 *   - A boundary we computed, compared against a value the student typed -> CLEAN THE BOUNDARY, then
 *     compare exactly. Widening the comparison would admit values strictly beyond the limit the
 *     teacher stated, which in a grading context is the wrong answer. Numerical's accepted ranges
 *     work this way.
 *   - Two computed values compared against each other (a resolved formula against a branch
 *     condition, a math-error candidate against the correct value, whether two intervals abut) ->
 *     an epsilon, because there is no authored decimal to round back to and both sides carry noise.
 *     That is what this is for.
 *
 * A few ULPs, relative, with an absolute floor. It absorbs binary representation noise and nothing
 * else: 0.1+0.2 is 0.30000000000000004 and sqrt(2)**2 is 2.0000000000000004.
 *
 * WHY 16 ULP. Measured, not guessed. Across realistic authored arithmetic -- percentage tolerances,
 * chained formulas through mathjs, trig identities, exp/log round trips, and the `min + i*step`
 * domain walk in formula-extrema -- the worst observed drift was 1 ULP and most cases were exact.
 * 16 leaves an order of magnitude of headroom while staying far below anything a person could type
 * deliberately. Repeated accumulation (`acc += step` a thousand times) does drift ~80 ULP, but
 * nothing here accumulates: the domain walk multiplies rather than sums, deliberately.
 *
 * This was 1e-9 relative -- around 280,000x looser than the measurement supports. Tightened once
 * the arithmetic was actually measured rather than guessed at.
 *
 * Lives here rather than in numerical.ts because the shared branching engine needs it too, and a
 * generic engine importing from one block's module is the wrong direction. `cleanComputed` moved
 * here for the same reason the moment `variableDomain` needed it.
 */
export const EPS = (n) => 16 * Number.EPSILON * Math.max(1, Math.abs(n));
/** True when two numbers differ only by representation noise. */
export function valuesMatchWithinEpsilon(a, b) {
    return Math.abs(a - b) <= EPS(Math.max(Math.abs(a), Math.abs(b)));
}
/**
 * Round a number WE computed back to a clean decimal, at 12 significant figures.
 *
 * Binary floating point puts noise in the last digit or two of a double's ~16: a 30% tolerance on
 * 1.15 computes as 1.49499999999999988, and a 10% tolerance on 0.07 as 0.0770000000000000129.
 * Compared sharply, the first marks a student who typed exactly 1.495 WRONG — which is the failure
 * an epsilon used to paper over by widening the comparison instead.
 *
 * Widening is the wrong cure, because it makes the boundary fuzzy in BOTH directions and so admits
 * values strictly greater than the limit the teacher stated. Cleaning the boundary fixes the same
 * problem at its source and leaves the comparison exact, so "at most 5.5" means at most 5.5.
 *
 * 12 is chosen with four digits to spare: doubles carry ~16 significant figures and the noise lives
 * in the last one or two, while no teacher can author a boundary needing a 13th significant digit
 * without typing 13 digits.
 *
 * The rule this encodes, and the one to keep applying: clean up OUR OWN arithmetic, never adjust
 * what the student submitted. A typed value is compared exactly as given.
 */
export function cleanComputed(n) {
    if (!Number.isFinite(n))
        return n;
    return parseFloat(n.toPrecision(12));
}
//# sourceMappingURL=epsilon.js.map